import {useMutation, useQuery} from '@apollo/client';
import styles from './dabaProReport.module.scss';
import {GET_COMPANY_DOCUMENTS} from 'graphql/queries/companyDocument';
import {toast} from 'react-toastify';
import {Dropdown, Form, Select, Table, Tag} from 'antd';
import {removePrefix} from 'utils/helper';
import moment from 'moment';
import {DELETE_SINGLE_COMPANY_DOCUMENT} from 'graphql/mutations/documentCenter';
import {useState} from 'react';
import {ViewUserDocument} from 'pages/adminUser/userDetails/tabs/documents/viewDocument';
import DBModal from 'components/modal/modal';
import {NavTabsEnum} from 'utils/constants';
import {useSelector} from 'react-redux';
import {makeSelectTabOperations} from 'redux/store/auth';
import dotsIcon from 'images/dots.svg';
import {Button} from '@dabafinance/react-components';
import {useNavigate} from 'react-router-dom';
import {ConfirmModal} from 'pages/adminOpportunities/stocksListings/ViewOrders/HandleOrders';

export const DabaProReport = () => {
  const [openDeleteModal, setOpenDeleteModal] = useState({});
  const [viewModal, setViewModal] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const {data, loading, refetch} = useQuery(GET_COMPANY_DOCUMENTS, {
    fetchPolicy: 'no-cache',
    onError: error => toast.error(error.message),
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
    variables: {
      filter: {
        isDabaPro: true,
        page: 1,
        limit: 10,
      },
    },
  });

  const tabOperations = useSelector(state =>
    makeSelectTabOperations(state, NavTabsEnum.DabaProReports),
  );

  const [deleteOneCompanyDocument, {loading: loadingDelete}] = useMutation(
    DELETE_SINGLE_COMPANY_DOCUMENT,
    {
      variables: {
        documentId: openDeleteModal?.id,
      },
    },
  );

  const columns = [
    {
      title: 'Document Name',
      dataIndex: 'documentName',
      key: 'documentName',
      fixed: 'left',
      width: 300,
      render: (text, record) => (
        <div className={styles.nameRowWidth}>
          <span>{removePrefix(record?.documentName)}</span>
          <Tag color={record?.language === 'EN' ? 'geekblue' : 'magenta'}>
            {record?.language}
          </Tag>
        </div>
      ),
    },
    {
      title: 'Document Category',
      dataIndex: 'documentCategory',
      key: 'documentCategory',
      width: 200,
      render: (text, record) => (
        <Tag color="blue">
          {record?.documentCategory?.name?.split('_')?.join(' ')}
        </Tag>
      ),
    },
    {
      title: 'Issued Date',
      dataIndex: 'issuedDate',
      key: 'issuedDate',
      width: 200,
      render: (text, record) => (
        <span style={{color: '#666', fontSize: '14px'}}>
          {record?.issuedDate
            ? moment(record?.issuedDate).format('DD MMM, YYYY h:mmA')
            : '-'}
        </span>
      ),
    },
    {
      title: 'Report Tag',
      dataIndex: 'tag',
      key: 'tag',
      width: 120,
      render: (text, record) => (
        <Tag
          color={
            record?.tag === 'STOCK'
              ? 'blue'
              : record?.tag === 'BOND'
              ? 'red'
              : record?.tag === 'MACRO'
              ? 'purple'
              : 'default'
          }>
          {record?.tag || '-'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      key: 'actions',
      fixed: 'right',
      width: 200,
      render: (text, record) => (
        <div className={styles.actions}>
          <Button
            onClick={() =>
              setViewModal({
                isOpen: true,
                documentUrl: record?.documentUrl,
                documentName: removePrefix(record?.documentName),
              })
            }
            type="secondary"
            label="View"
            className={styles['deactivate-btn']}
          />
          <Dropdown
            trigger="click"
            menu={{
              items: [
                {
                  label: 'Edit Document',
                  onClick: () =>
                    navigate(`/dashboard/daba-pro/update/${record?.id}`),
                  disabled: !tabOperations?.update,
                },
                {
                  label: 'Delete Document',
                  onClick: () =>
                    setOpenDeleteModal({isOpen: true, id: record?.id}),
                  disabled: !tabOperations?.delete,
                },
              ],
            }}>
            <div>
              <img src={dotsIcon} alt="More options" />
            </div>
          </Dropdown>
        </div>
      ),
    },
  ];

  const handlePageChange = (page, pageSize) => {
    if (page === currentPage && pageSize === limit) return;
    setCurrentPage(page);
    setLimit(pageSize);
    refetch({
      filter: {
        page,
        limit: pageSize,
        isDabaPro: true,
      },
    });
  };

  const handleDeleteCompanyDocument = () => {
    deleteOneCompanyDocument({
      variables: {
        documentId: openDeleteModal?.id,
      },
    })
      .then(({data: deleteOneCompanyDocument}) => {
        if (deleteOneCompanyDocument.__typename === 'Error') {
          toast.error(
            `Something went wrong!!! - ${deleteOneCompanyDocument.message} `,
          );
        } else {
          toast.success('Document Deleted Successfully');
          setOpenDeleteModal(false);
          refetch();
        }
      })
      .catch(err => toast.error(`Something went wrong!! - ${err}`));
  };

  const handleApply = () => {
    setCurrentPage(1);
    setLimit(10);
    refetch({
      filter: {
        tag: form.getFieldsValue().tag,
        page: 1,
        limit: 10,
        isDabaPro: true,
      },
    });
  };
  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <h1>Daba Pro Report Documents</h1>
        <Button
          type="primary"
          disabled={!tabOperations?.create}
          label="Add Document"
          onClick={() => navigate('/dashboard/daba-pro/create')}
        />
      </div>
      <div className={styles.filterSection}>
        <Form form={form} layout="inline">
          <Form.Item name="tag" label="Report Tag">
            <Select placeholder="Select Report Tag">
              <Select.Option value="STOCK">STOCK</Select.Option>
              <Select.Option value="BOND">BOND</Select.Option>
              <Select.Option value="MACRO">MACRO</Select.Option>
            </Select>
          </Form.Item>
          <Button type="secondary" label="Apply" onClick={handleApply} />
        </Form>
      </div>
      <div className={styles.tableContainer}>
        <Table
          dataSource={data?.getCompanyDocument?.data}
          loading={loading}
          columns={columns}
          pagination={{
            current: currentPage,
            total: data?.getCompanyDocument?.pagination?.total,
            pageSize: limit,
            onChange: handlePageChange,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
          }}
          rowKey="id"
        />
      </div>
      <DBModal
        isOpen={viewModal.isOpen}
        handleClose={() => setViewModal({isOpen: false})}
        width={900}
        content={
          <ViewUserDocument
            url={viewModal?.documentUrl}
            documentName={viewModal?.documentName}
          />
        }
      />
      <ConfirmModal
        openModal={openDeleteModal?.isOpen}
        setOpenModal={setOpenDeleteModal}
        handleOperation={handleDeleteCompanyDocument}
        title="Delete Daba Pro Report Document"
        confirmText="Are you sure you want to delete this Daba Pro Report Document?"
        loading={loadingDelete}
      />
    </div>
  );
};
